import React, {
    FC,
    MutableRefObject,
    ReactElement,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useSearchParams } from 'react-router-dom'
import FilterListIcon from '@material-ui/icons/FilterList'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import {
    AdminFilters,
    AdminFilterValues,
    AdminFiltersSlotRenderArgs,
    SelectFilterNames
} from '../../Molecules/AdminFilters/AdminFilters'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { displayError } from '../../../../React/Common/Utils/Messages'
import {
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    PartitionModal,
    PartitionListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import { ReportingAccessLevels } from '../../../../NonReact/constants/Permissions'
import { getPartitionList } from '../../../../React/Common/API/ItemsGetters'
import {
    PartitionList as PartitionRows,
    PartitionListItem,
    PartitionListResponse,
    PartitionListRow
} from '../../../../NonReact/Types/PartitionListTypes'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import {
    ADMIN_URL_QUERY_CME,
    ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN,
    ADMIN_URL_QUERY_PARTITION,
    ADMIN_URL_QUERY_TELEPHONY_SERVER,
    AdminListUrlBootstrap,
    buildAdminListPathWithQuery,
    parseAdminListUrlSearchParams
} from '../../../Utils/adminListUrlParams'
import { hasResolvedInitialAdminScopeFilters } from '../../../Utils/adminListFilterReadiness'
import { PartitionConfigurationModal } from '../../../../React/PartitionConfiguration/Modal/PartitionConfigurationModal'
import { getPartitionLicensedForSelectLabel } from '../../../../React/PartitionConfiguration/Utils/partitionLicensedForLabels'
import { getPartitionListSyncTypeDisplayLabel } from '../../../../React/PartitionConfiguration/Utils/partitionListSyncTypeDisplay'
import { SelectFilter, SelectFilterOption } from '../../Molecules/AdminFilters/SelectFilter'
import { AdminListCountLink } from '../../Atoms/AdminListCountLink/AdminListCountLink'
import { RouteID } from '../../Molecules/Header/Types/Types'
import { useBranding } from '../../../../React/Common/Hooks/useBranding'
import { Branding } from '../../../../React/Common/API/BrandingAPI'
import { usePromiseValue } from '../../../../React/Common/Hooks/usePromiseValue'
import { buildLegacyAdminDevicesListHref, buildLegacyAdminUsersListHref } from '../../../Utils/adminLegacyServletUrls'

/**
 * True when the filter value is a concrete TelSys selection (excludes [All] / Any placeholders).
 *
 * @param value - Raw telephony option value from admin filters.
 * @returns Whether the value should be sent to the list API as a restricting parameter.
 */
const isConcreteTelSysFilterValue = (value: unknown): boolean => {
    if (IsVarEmpty(value)) {
        return false
    }

    if (value === ALL_VALUE) return false

    if (value === ANY_VALUE_STRING_NUMERIC) return false

    return true
}

const ADMIN_FILTERS_SESSION_STORAGE_KEY: string = 'adminFilters.selectedScope'

/**
 * Reads persisted Telephony Server bootstrap value from admin filter session storage.
 *
 * @returns Stored Telephony Server value when present, otherwise undefined.
 */
const readPersistedTelephonyServerBootstrap = (): Definable<string> => {
    try {
        const rawState: Nullable<string> = window.sessionStorage.getItem(ADMIN_FILTERS_SESSION_STORAGE_KEY)

        if (!isStringNotEmpty(rawState ?? EMPTY_STRING)) {
            return undefined
        }

        const parsedState: unknown = JSON.parse(rawState as string)

        if (IsVarEmpty(parsedState) || typeof parsedState !== 'object') {
            return undefined
        }

        const telephonyServer: unknown = (parsedState as { telephonyServer?: unknown }).telephonyServer

        const telephonyServerStr: string =
            typeof telephonyServer === 'string' || typeof telephonyServer === 'number'
                ? String(telephonyServer)
                : EMPTY_STRING

        return isStringNotEmpty(telephonyServerStr) ? telephonyServerStr : undefined
    } catch {
        return undefined
    }
}

/**
 * Ordered partition licence values shown by the list filter.
 */
const PARTITION_LIST_LICENCE_FILTER_VALUES: readonly ReportingAccessLevels[] = [
    ReportingAccessLevels.LITE,
    ReportingAccessLevels.ONE_THOUSAND,
    ReportingAccessLevels.TWO_THOUSAND,
    ReportingAccessLevels.ENTERPRISE,
    ReportingAccessLevels.ESSENTIALS,
    ReportingAccessLevels.PROFESSIONAL,
    ReportingAccessLevels.CALL_CENTRE_AGENT,
    ReportingAccessLevels.CALL_CENTRE_SUPERVISOR,
    ReportingAccessLevels.BLF,
    ReportingAccessLevels.ONE,
    ReportingAccessLevels.NONE
]

/**
 * Routed Partitions list view.
 *
 * @returns Partitions list page content.
 */
export const PartitionList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)
    language.loadBundle(PartitionListLanguage.NAME)
    language.loadBundle(PartitionModal.NAME)

    const {
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()
    const branding: Nullable<Branding> = useBranding()

    const [searchParams] = useSearchParams()
    const urlBootstrap: Nullable<AdminListUrlBootstrap> = useMemo((): Nullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(searchParams)

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [searchParams])
    const [hasResolvedInitialUrlBootstrapLoad, setHasResolvedInitialUrlBootstrapLoad]: ReactState<boolean> =
        useState<boolean>(() => IsVarEmpty(urlBootstrap))
    const [hasResolvedInitialFiltersLoad, setHasResolvedInitialFiltersLoad]: ReactState<boolean> =
        useState<boolean>(false)
    const [persistedTelephonyServerBootstrap]: ReactState<Nullable<string>> = useState<Nullable<string>>(() =>
        readPersistedTelephonyServerBootstrap()
    )
    const [isPartitionModalOpen, setIsPartitionModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [isViewOnlyModal, setIsViewOnlyModal]: ReactState<boolean> = useState<boolean>(false)
    const [isCommonDevicesEditUnlocked, setIsCommonDevicesEditUnlocked]: ReactState<boolean> = useState<boolean>(false)
    const [selectedPartitionRow, setSelectedPartitionRow]: ReactState<Nullable<PartitionListRow>> =
        useState<Nullable<PartitionListRow>>(null)
    const isBootstrappingFromUrl: boolean = isVarNotEmpty(urlBootstrap) && IsVarEmpty(filters)
    const hasTelephonyServerBootstrap: boolean =
        isStringNotEmpty(urlBootstrap?.telephonyServer ?? EMPTY_STRING) ||
        isStringNotEmpty(persistedTelephonyServerBootstrap ?? EMPTY_STRING)
    const [isAwaitingInitialBootstrappedListResult, setIsAwaitingInitialBootstrappedListResult]: ReactState<boolean> =
        useState<boolean>(hasTelephonyServerBootstrap)
    const isWaitingForInitialUrlBootstrapLoad: boolean =
        isVarNotEmpty(urlBootstrap) && !hasResolvedInitialUrlBootstrapLoad
    const isWaitingForInitialFiltersLoad: boolean = !hasResolvedInitialFiltersLoad

    const cmeFilterValue: Definable<string | number> = filters?.cme?.value
    const listCmeId: Nullable<string> = IsVarEmpty(cmeFilterValue)
        ? userInfo?.Scope?.CMEnvironmentID ?? null
        : String(cmeFilterValue)
    const listSearch: string = filters?.search ?? EMPTY_STRING
    const telSysFilterValue: Definable<string | number> = filters?.telephonyServer?.value
    const listTelSysId: Definable<string | number> = isConcreteTelSysFilterValue(telSysFilterValue)
        ? telSysFilterValue
        : undefined
    const listNoTelephonyServer: boolean = filters?.noTelephonyServer ?? false
    const listIncludeRecursive: boolean = filters?.includeCmeChildren ?? true
    const listLicenceFilter: Definable<string> =
        isVarNotEmpty(filters?.licenceType?.value) && filters.licenceType.value !== ALL_VALUE
            ? String(filters.licenceType.value)
            : undefined

    useAdminListLegacyServletSwitch(ServletCmds.ADMIN_PARTITIONS, filters, userInfo?.Scope?.CMEnvironmentID)

    const shouldRequestPartitions: boolean =
        !isWaitingForInitialFiltersLoad &&
        !isBootstrappingFromUrl &&
        !IsVarEmpty(listCmeId) &&
        !listNoTelephonyServer &&
        isVarNotEmpty(listTelSysId)
    const cmeIdForCountLinks: string = String(listCmeId ?? userInfo?.Scope?.CMEnvironmentID ?? EMPTY_STRING)
    const [currentPage, rowsPerPage]: PaginationInfo = pagination
    const searchQuery: Definable<string> = listSearch === EMPTY_STRING ? undefined : listSearch
    const fetchPartitionList = useCallback(
        (): Promise<Nullable<PartitionListResponse>> =>
            getPartitionList(
                listTelSysId as string | number,
                currentPage * rowsPerPage,
                rowsPerPage,
                sort,
                searchQuery,
                undefined,
                listLicenceFilter
            ),
        [currentPage, listLicenceFilter, listTelSysId, rowsPerPage, searchQuery, sort]
    )
    const {
        value: partitionListResponse,
        loading: partitionListLoading,
        error: partitionListError,
        refresh: refreshPartitionList
    } = usePromiseValue<Nullable<PartitionListResponse>>(
        fetchPartitionList,
        {
            initialValue: null,
            enabled: shouldRequestPartitions
        },
        [currentPage, rowsPerPage, sort, searchQuery, listLicenceFilter, listTelSysId, shouldRequestPartitions]
    )
    /**
     * True after filters/session hydration when a list request should run but {@link usePromiseValue} has not yet
     * flipped `loading` (or returned the first payload). Keeps the table skeleton on without a flash of empty rows.
     */
    const isPartitionListAwaitingFirstResult: boolean =
        shouldRequestPartitions &&
        !partitionListLoading &&
        IsVarEmpty(partitionListError) &&
        IsVarEmpty(partitionListResponse)
    const partitionList: PartitionRows = useMemo(
        (): PartitionRows =>
            partitionListResponse?.Data?.Items?.map(
                (item: PartitionListItem, index: number): PartitionListRow => ({
                    key: item.PartIDInTelSys ?? `partition-row-${index}`,
                    TelSysID: listTelSysId as string | number,
                    PartitionID: item.PartitionID ?? EMPTY_STRING,
                    Description: item.Description ?? EMPTY_STRING,
                    PartIDInTelSys: item.PartIDInTelSys ?? EMPTY_STRING,
                    LicensedForRptUserType: item.LicensedForRptUserType ?? EMPTY_STRING,
                    ExtCount: item.ExtCount ?? EMPTY_STRING,
                    UserCount: item.UserCount ?? EMPTY_STRING,
                    AcdAgentCount: item.AcdAgentCount ?? EMPTY_STRING,
                    SyncType: item.SyncType ?? EMPTY_STRING,
                    CommsStatus: item.CommsStatus ?? EMPTY_STRING
                })
            ) ?? [],
        [listTelSysId, partitionListResponse]
    )
    const commonDevicesPartitionIds: Set<string> = useMemo(
        (): Set<string> =>
            new Set<string>(
                partitionListResponse?.Data?.Items?.filter(
                    (item: PartitionListItem): boolean => item.IsGlobal === true
                ).map((item: PartitionListItem): string => String(item.PartIDInTelSys ?? EMPTY_STRING)) ?? []
            ),
        [partitionListResponse]
    )
    const canEditPartitionRow = useCallback(
        (row: Definable<PartitionListRow>): boolean => {
            if (IsVarEmpty(row)) {
                return false
            }

            return isCommonDevicesEditUnlocked || !commonDevicesPartitionIds.has(String(row.PartIDInTelSys))
        },
        [commonDevicesPartitionIds, isCommonDevicesEditUnlocked]
    )

    const licenceFilterOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            {
                value: ALL_VALUE,
                label: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_OPTION_NONE_LABEL)
            },
            ...PARTITION_LIST_LICENCE_FILTER_VALUES.map(
                (licence: ReportingAccessLevels): SelectFilterOption => ({
                    value: licence,
                    label: getPartitionLicensedForSelectLabel(language, licence)
                })
            )
        ],
        [language]
    )

    const getPartitions = useCallback((): void => {
        if (!shouldRequestPartitions) {
            setTotalCount(0)
            setHasResolvedInitialUrlBootstrapLoad(true)
            return
        }

        refreshPartitionList().catch((): void => undefined)
    }, [refreshPartitionList, setTotalCount, shouldRequestPartitions])

    useEffect((): void => {
        if (IsVarEmpty(partitionListError)) {
            return
        }

        displayError(partitionListError)
    }, [partitionListError])

    useEffect((): void => {
        if (!shouldRequestPartitions) {
            setHasResolvedInitialUrlBootstrapLoad(true)
            return
        }

        if (partitionListLoading) return

        setTotalCount(Number(partitionListResponse?.Data?.Total) || 0)
        setHasResolvedInitialUrlBootstrapLoad(true)
    }, [partitionListLoading, partitionListResponse, setTotalCount, shouldRequestPartitions])

    useEffect((): void => {
        if (!isAwaitingInitialBootstrappedListResult) return

        if (listNoTelephonyServer) {
            setIsAwaitingInitialBootstrappedListResult(false)
            return
        }

        if (isVarNotEmpty(partitionListResponse) || isVarNotEmpty(partitionListError)) {
            setIsAwaitingInitialBootstrappedListResult(false)
        }
    }, [isAwaitingInitialBootstrappedListResult, listNoTelephonyServer, partitionListError, partitionListResponse])

    const columns: DataTableColumn<PartitionListRow, keyof PartitionListRow>[] = useMemo(
        (): DataTableColumn<PartitionListRow, keyof PartitionListRow>[] => [
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_IDInTelSys),
                field: 'PartIDInTelSys',
                key: 'partition-list-part-id',
                sortable: true
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_Description),
                field: 'Description',
                key: 'partition-list-description',
                sortable: true,
                align: 'left',
                renderCell: (row: PartitionListRow): ReactNode =>
                    canEditPartitionRow(row) ? (
                        <span
                            className='router-datatable-body-clickable partition-list-description-clickable'
                            onMouseDown={(event: React.MouseEvent): void => {
                                event.preventDefault()
                                setSelectedPartitionRow(row)
                                setIsViewOnlyModal(false)
                                setIsPartitionModalOpen(true)
                            }}
                        >
                            {row.Description}
                        </span>
                    ) : (
                        row.Description
                    )
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_Licence),
                field: 'LicensedForRptUserType',
                key: 'partition-list-licence',
                sortable: true
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_Extensions),
                field: 'ExtCount',
                key: 'partition-list-ext',
                sortable: false,
                disableRowSelection: true,
                renderCell: (row: PartitionListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.ExtCount}
                        href={buildLegacyAdminDevicesListHref(
                            branding?.URLMapping,
                            cmeIdForCountLinks,
                            Number(row.TelSysID),
                            listIncludeRecursive,
                            row.PartitionID
                        )}
                        ariaLabel={language.get(PartitionListLanguage.PartitionList_CountLink_ExtensionsAria)}
                    />
                )
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_AcdAgents),
                field: 'AcdAgentCount',
                key: 'partition-list-agents',
                sortable: false,
                disableRowSelection: true,
                renderCell: (row: PartitionListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.AcdAgentCount}
                        to={buildAdminListPathWithQuery(`/${RouteID.AGENT}`, {
                            [ADMIN_URL_QUERY_CME]: cmeIdForCountLinks,
                            [ADMIN_URL_QUERY_TELEPHONY_SERVER]: String(row.TelSysID),
                            [ADMIN_URL_QUERY_PARTITION]: String(row.PartIDInTelSys),
                            [ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN]: listIncludeRecursive ? 'true' : 'false'
                        })}
                        ariaLabel={language.get(PartitionListLanguage.PartitionList_CountLink_AcdAgentsAria)}
                    />
                )
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_AppUsers),
                field: 'UserCount',
                key: 'partition-list-users',
                sortable: false,
                disableRowSelection: true,
                renderCell: (row: PartitionListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.UserCount}
                        href={buildLegacyAdminUsersListHref(
                            branding?.URLMapping,
                            cmeIdForCountLinks,
                            Number(row.TelSysID),
                            listIncludeRecursive,
                            row.PartitionID
                        )}
                        ariaLabel={language.get(PartitionListLanguage.PartitionList_CountLink_AppUsersAria)}
                    />
                )
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_SyncType),
                field: 'SyncType',
                key: 'partition-list-sync',
                sortable: true,
                renderCell: (row: PartitionListRow): ReactNode =>
                    getPartitionListSyncTypeDisplayLabel(language, row.SyncType)
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_Status),
                field: 'CommsStatus',
                key: 'partition-list-status',
                sortable: true
            }
        ],
        [
            branding?.URLMapping,
            canEditPartitionRow,
            cmeIdForCountLinks,
            language,
            listIncludeRecursive
        ]
    )

    const openAddModal = useCallback((): void => {
        setSelectedPartitionRow(null)
        setIsViewOnlyModal(false)
        setIsPartitionModalOpen(true)
    }, [])

    const openChangeModal = useCallback(
        (selectedRows: PartitionListRow[]): void => {
            const selectedRow: Nullable<PartitionListRow> = selectedRows[0] ?? null
            const isCommonDevicesPartition: boolean =
                isVarNotEmpty(selectedRow) && commonDevicesPartitionIds.has(String(selectedRow.PartIDInTelSys))
            if (isCommonDevicesPartition && !isCommonDevicesEditUnlocked) return

            setSelectedPartitionRow(selectedRow)
            setIsViewOnlyModal(false)
            setIsPartitionModalOpen(true)
        },
        [commonDevicesPartitionIds, isCommonDevicesEditUnlocked]
    )

    const openViewModal = useCallback((selectedRows: PartitionListRow[]): void => {
        setSelectedPartitionRow(selectedRows[0] ?? null)
        setIsViewOnlyModal(true)
        setIsPartitionModalOpen(true)
    }, [])

    const closePartitionModal = useCallback((): void => {
        setIsPartitionModalOpen(false)
        setSelectedPartitionRow(null)
        setIsViewOnlyModal(false)
    }, [])

    const previousUrlBootstrapKeyRef: MutableRefObject<string> = useRef<string>(EMPTY_STRING)

    useEffect((): void => {
        const nextKey: string = IsVarEmpty(urlBootstrap) ? EMPTY_STRING : JSON.stringify(urlBootstrap)

        if (nextKey === previousUrlBootstrapKeyRef.current) return

        previousUrlBootstrapKeyRef.current = nextKey

        if (IsVarEmpty(urlBootstrap)) {
            setHasResolvedInitialUrlBootstrapLoad(true)
        } else {
            setHasResolvedInitialUrlBootstrapLoad(false)
        }
    }, [urlBootstrap])

    useHotkeys(
        'ctrl+alt+/',
        (event: KeyboardEvent): void => {
            event.preventDefault()
            setIsCommonDevicesEditUnlocked(true)
        },
        { enableOnFormTags: true },
        []
    )

    return (
        <div className='partition-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_partitions} />
            <div className='partition-list-table-container admin-table-list-container'>
                <AdminFilters
                    labelTelSysPartitionAllSentinelAsNone
                    selectFilterPlaceholderTooltips={{
                        [SelectFilterNames.TELEPHONY_SERVER]: true
                    }}
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL)
                    }}
                    urlBootstrap={urlBootstrap}
                    onChange={(values: AdminFilterValues): void => {
                        setFilters(values)
                        if (hasResolvedInitialAdminScopeFilters(values)) {
                            setHasResolvedInitialFiltersLoad(true)
                        }
                    }}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    additionalFilterSkeletonCount={1}
                    additionalFilters={({
                        isLoading,
                        filterValues,
                        onSelectFilterChange
                    }: AdminFiltersSlotRenderArgs): ReactElement => ( // NOSONAR
                        <div>
                            <SelectFilter
                                onChange={(value: Nullable<SelectFilterOption>): void => {
                                    onSelectFilterChange(SelectFilterNames.LICENCE_TYPE, value)
                                }}
                                options={licenceFilterOptions}
                                label={language.get(PartitionListLanguage.PartitionList_Filter_Licence)}
                                icon={<FilterListIcon />}
                                disabled={isLoading}
                                value={filterValues.licenceType ?? null}
                                defaultValue={licenceFilterOptions[0]?.value}
                            />
                        </div>
                    )}
                    onRefresh={getPartitions}
                />
                <DataTable<PartitionListRow, keyof PartitionListRow>
                    permissionName={AdminBackendPermissionNames.PARTITION}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={partitionList}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={
                        partitionListLoading ||
                        isWaitingForInitialUrlBootstrapLoad ||
                        isWaitingForInitialFiltersLoad ||
                        isAwaitingInitialBootstrappedListResult ||
                        isPartitionListAwaitingFirstResult
                    }
                    paginationServer
                    sortServer
                    isSelectable
                    onSortChange={setSort}
                    toolbarAddDisabled={IsVarEmpty(listTelSysId)}
                    toolbarAddDisabledTitle={language.get(
                        PartitionListLanguage.PartitionList_Add_Disabled_SelectTelephonyServer
                    )}
                    onToolbarAddClick={openAddModal}
                    onToolbarChangeClick={openChangeModal}
                    onToolbarViewClick={openViewModal}
                    onRowsPerPageChange={(nextPage: number, nextRowsPerPage: number): void =>
                        setPagination([nextPage, nextRowsPerPage])
                    }
                    onPageChange={(nextPage: number, nextRowsPerPage: number): void =>
                        setPagination([nextPage, nextRowsPerPage])
                    }
                />
                {isPartitionModalOpen && isVarNotEmpty(listTelSysId) ? (
                    <PartitionConfigurationModal
                        key={`partition-modal-${selectedPartitionRow?.PartIDInTelSys ?? 'add'}-${
                            isViewOnlyModal ? 'view' : 'edit'
                        }`}
                        refreshAction={getPartitions}
                        filterTelSysID={Number(selectedPartitionRow?.TelSysID ?? listTelSysId)}
                        selectedPartitionIDInTelSys={
                            isVarNotEmpty(selectedPartitionRow?.PartIDInTelSys)
                                ? Number(selectedPartitionRow.PartIDInTelSys)
                                : undefined
                        }
                        viewOnly={isViewOnlyModal}
                        onModalExited={closePartitionModal}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
